<template>
  <div>
    <!-- Top Filter Header -->
    <v-card class="mb-6 elevation-1">
      <v-card-title class="d-flex align-center py-4 px-6">
        <v-icon color="error" class="me-2">mdi-cancel</v-icon>
        <span class="font-weight-bold">الرحلات الملغاة</span>
        <v-chip color="error" class="ms-3 font-weight-bold" small label v-if="totalTrips > 0">
          {{ totalTrips }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined small @click="fetchTrips" class="ms-2">
          <v-icon left size="16">mdi-refresh</v-icon>
          تحديث
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Cancellation Type Filter Tabs -->
      <v-tabs v-model="filterTypeTab" class="px-4" @change="onTypeTabChange">
        <v-tab key="all">الكل</v-tab>
        <v-tab key="driver_no_start">عدم بدء الرحلة</v-tab>
        <v-tab key="emergency">طوارئ</v-tab>
      </v-tabs>
    </v-card>

    <!-- Main Table Card -->
    <v-card elevation="1">
      <v-card-title class="pa-4">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث في الرحلات الملغاة (رقم الرحلة، الكابتن، المسار، السبب)..."
          single-line
          hide-details
          outlined
          dense
          clearable
          @input="onSearchInput"
          @click:clear="onSearchClear"
        ></v-text-field>
      </v-card-title>

      <!-- Error State -->
      <v-alert v-if="errorMessage" type="error" dismissible class="mx-4 my-2">
        <div class="d-flex align-center">
          <span>{{ errorMessage }}</span>
          <v-spacer></v-spacer>
          <v-btn small outlined color="white" @click="fetchTrips">إعادة المحاولة</v-btn>
        </div>
      </v-alert>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="trips"
        :loading="loading"
        loading-text="جاري تحميل الرحلات الملغاة..."
        no-data-text="لا توجد رحلات ملغاة"
        hide-default-footer
        class="elevation-0"
      >
        <!-- Planned Trip ID -->
        <template v-slot:item.planned_trip_id="{ item }">
          <span class="font-weight-bold primary--text">#{{ item.planned_trip_id || item.id }}</span>
        </template>

        <!-- Driver -->
        <template v-slot:item.driver="{ item }">
          <div>
            <div class="font-weight-bold text-subtitle-2">
              {{ item.driver ? item.driver.name : 'غير محدد' }}
            </div>
            <small v-if="item.driver && item.driver.tel_number" class="text-caption grey--text">
              {{ item.driver.tel_number }}
            </small>
          </div>
        </template>

        <!-- Route -->
        <template v-slot:item.route="{ item }">
          <span class="font-weight-medium">
            {{ item.route ? item.route.name : 'غير محدد' }}
          </span>
        </template>

        <!-- Scheduled Start Time -->
        <template v-slot:item.scheduled_start_time="{ item }">
          <span class="text-body-2">
            {{ item.scheduled_start_time || '-' }}
          </span>
        </template>

        <!-- Cancelled At -->
        <template v-slot:item.cancelled_at="{ item }">
          <span class="text-body-2 error--text font-weight-medium">
            {{ item.cancelled_at || '-' }}
          </span>
        </template>

        <!-- Cancellation Type -->
        <template v-slot:item.cancellation_type="{ item }">
          <v-chip
            :color="getCancellationTypeInfo(item.cancellation_type).color"
            dark
            small
            class="font-weight-bold"
          >
            {{ getCancellationTypeInfo(item.cancellation_type).label }}
          </v-chip>
        </template>

        <!-- Cancellation Reason -->
        <template v-slot:item.cancellation_reason="{ item }">
          <v-tooltip bottom max-width="350px">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="text-truncate text-caption"
                style="max-width: 220px;"
              >
                {{ item.cancellation_reason || '-' }}
              </div>
            </template>
            <span>{{ item.cancellation_reason || '-' }}</span>
          </v-tooltip>
        </template>

        <!-- Trip Status -->
        <template v-slot:item.trip_status="{ item }">
          <v-chip color="error" outlined small class="font-weight-bold">
            ملغاة
          </v-chip>
        </template>

        <!-- Target Stop Snapshot -->
        <template v-slot:item.cancelled_stop="{ item }">
          <span class="font-weight-medium">
            {{ item.cancelled_stop ? item.cancelled_stop.name : '-' }}
          </span>
        </template>

        <!-- Distance Snapshot -->
        <template v-slot:item.cancelled_distance_meters="{ item }">
          <v-chip
            :color="item.location_available && item.cancelled_distance_meters !== null ? 'info' : 'grey'"
            outlined
            x-small
            class="font-weight-bold"
          >
            {{ formatDistance(item) }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            color="primary"
            small
            outlined
            @click="openDetailsModal(item)"
          >
            <v-icon left size="16">mdi-eye-outline</v-icon>
            عرض التفاصيل
          </v-btn>
        </template>
      </v-data-table>

      <!-- Pagination Footer -->
      <v-divider></v-divider>
      <div class="d-flex align-center justify-space-between pa-4" v-if="totalPages > 1">
        <span class="text-caption grey--text">
          عرض الصفحة {{ currentPage }} من {{ totalPages }} (إجمالي الرحلات الملغاة: {{ totalTrips }})
        </span>
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
          circle
          @input="fetchTrips"
        ></v-pagination>
      </div>
    </v-card>

    <!-- Details Modal -->
    <v-dialog v-model="detailsDialog" max-width="650">
      <v-card v-if="selectedItem">
        <v-card-title class="headline error--text d-flex align-center">
          <v-icon color="error" class="me-2">mdi-cancel</v-icon>
          تفاصيل الرحلة الملغاة #{{ selectedItem.planned_trip_id || selectedItem.id }}
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <v-row dense>
            <!-- Trip ID -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">رقم الرحلة</div>
              <div class="font-weight-bold text-subtitle-1">#{{ selectedItem.planned_trip_id || selectedItem.id }}</div>
            </v-col>

            <!-- Status -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">الحالة الأخيرة</div>
              <v-chip color="error" dark small class="font-weight-bold mt-1">ملغاة</v-chip>
            </v-col>

            <!-- Driver Name -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">الكابتن (السائق)</div>
              <div class="font-weight-bold">{{ selectedItem.driver ? selectedItem.driver.name : 'غير محدد' }}</div>
            </v-col>

            <!-- Driver Phone -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">رقم هاتف الكابتن</div>
              <div class="font-weight-bold">{{ (selectedItem.driver && selectedItem.driver.tel_number) ? selectedItem.driver.tel_number : 'غير متوفر' }}</div>
            </v-col>

            <!-- Route -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">المسار</div>
              <div class="font-weight-bold">{{ selectedItem.route ? selectedItem.route.name : 'غير محدد' }}</div>
            </v-col>

            <!-- Bus -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">الحافلة المركبة</div>
              <div class="font-weight-bold">
                {{ selectedItem.bus ? (selectedItem.bus.name + (selectedItem.bus.bus_number ? ' (' + selectedItem.bus.bus_number + ')' : '')) : 'غير محدد' }}
              </div>
            </v-col>

            <!-- Scheduled Start Time -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">موعد الرحلة الرسمي</div>
              <div class="font-weight-bold">{{ selectedItem.scheduled_start_time || '-' }}</div>
            </v-col>

            <!-- Movement Started At -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">وقت بدء التحرك</div>
              <div class="font-weight-bold">{{ selectedItem.movement_started_at || selectedItem.started_at || 'لم يبدأ التحرك' }}</div>
            </v-col>

            <!-- Cancelled At -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">وقت الإلغاء</div>
              <div class="font-weight-bold error--text">{{ selectedItem.cancelled_at || '-' }}</div>
            </v-col>

            <!-- Cancellation Type -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">نوع الإلغاء</div>
              <v-chip
                :color="getCancellationTypeInfo(selectedItem.cancellation_type).color"
                dark
                small
                class="font-weight-bold mt-1"
              >
                {{ getCancellationTypeInfo(selectedItem.cancellation_type).label }}
              </v-chip>
            </v-col>

            <!-- Cancellation Reason (Full) -->
            <v-col cols="12" class="py-2">
              <div class="text-caption grey--text">سبب الإلغاء التفصيلي</div>
              <v-alert type="error" outlined class="mt-1 mb-0 py-2 px-3 text-body-2">
                {{ selectedItem.cancellation_reason || 'غير محدد' }}
              </v-alert>
            </v-col>

            <!-- Target Stop Snapshot -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">المحطة المستهدفة وقت الإلغاء</div>
              <div class="font-weight-bold">
                <v-icon size="16" color="primary" class="me-1">mdi-map-marker</v-icon>
                {{ selectedItem.cancelled_stop ? selectedItem.cancelled_stop.name : '-' }}
              </div>
            </v-col>

            <!-- Distance Snapshot -->
            <v-col cols="12" sm="6" class="py-2">
              <div class="text-caption grey--text">المسافة عن المحطة وقت الإلغاء</div>
              <div class="font-weight-bold">
                <v-icon size="16" color="info" class="me-1">mdi-map-marker-distance</v-icon>
                {{ formatDistance(selectedItem) }}
              </div>
            </v-col>

            <!-- GPS Coordinates Snapshot -->
            <v-col cols="12" class="py-2">
              <div class="text-caption grey--text">موقع السائق وقت الإلغاء (الإحداثيات)</div>
              <div class="font-weight-bold text-caption grey--text text--darken-2">
                <v-icon size="16" class="me-1">mdi-crosshairs-gps</v-icon>
                <template v-if="selectedItem.location_available">
                  خط العرض: {{ selectedItem.cancelled_position_lat }}، خط الطول: {{ selectedItem.cancelled_position_lng }}
                </template>
                <template v-else>
                  الموقع غير متاح
                </template>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="secondary" depressed @click="detailsDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'CancelledTrips',
  data() {
    return {
      filterTypeTab: 0, // 0: all, 1: driver_no_start, 2: emergency
      search: '',
      searchTimeout: null,
      loading: false,
      errorMessage: '',
      trips: [],
      currentPage: 1,
      totalPages: 1,
      totalTrips: 0,
      perPage: 15,
      detailsDialog: false,
      selectedItem: null,
      headers: [
        { text: 'رقم الرحلة', value: 'planned_trip_id', align: 'start', sortable: false },
        { text: 'الكابتن (السائق)', value: 'driver', sortable: false },
        { text: 'المسار', value: 'route', sortable: false },
        { text: 'موعد الرحلة', value: 'scheduled_start_time', sortable: false },
        { text: 'وقت الإلغاء', value: 'cancelled_at', sortable: false },
        { text: 'نوع الإلغاء', value: 'cancellation_type', align: 'center', sortable: false },
        { text: 'سبب الإلغاء', value: 'cancellation_reason', sortable: false },
        { text: 'الحالة', value: 'trip_status', align: 'center', sortable: false },
        { text: 'المحطة المستهدفة', value: 'cancelled_stop', sortable: false },
        { text: 'المسافة وقت الإلغاء', value: 'cancelled_distance_meters', align: 'center', sortable: false },
        { text: 'الإجراءات', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  mounted() {
    this.fetchTrips();
  },
  methods: {
    getTypeParam() {
      if (this.filterTypeTab === 1) return 'driver_no_start';
      if (this.filterTypeTab === 2) return 'emergency';
      return 'all';
    },
    onTypeTabChange() {
      this.currentPage = 1;
      this.fetchTrips();
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchTrips();
      }, 400);
    },
    onSearchClear() {
      this.search = '';
      this.currentPage = 1;
      this.fetchTrips();
    },
    fetchTrips() {
      this.loading = true;
      this.errorMessage = '';

      const params = {
        page: this.currentPage,
        per_page: this.perPage,
        type: this.getTypeParam(),
      };

      if (this.search && this.search.trim() !== '') {
        params.search = this.search.trim();
      }

      axios
        .get('/admin/cancelled-trips', { params })
        .then((response) => {
          this.loading = false;
          if (response.data && response.data.success && response.data.data) {
            const paginated = response.data.data;
            this.trips = paginated.data || [];
            this.currentPage = paginated.current_page || 1;
            this.totalPages = paginated.last_page || 1;
            this.totalTrips = paginated.total || 0;
            this.$emit('updateCount', this.totalTrips);
          } else {
            this.trips = [];
            this.totalTrips = 0;
            this.totalPages = 1;
            this.$emit('updateCount', 0);
          }
        })
        .catch((error) => {
          this.loading = false;
          console.error('Error fetching cancelled trips:', error);
          const msg = (error.response && error.response.data && error.response.data.message)
            ? error.response.data.message
            : 'حدث خطأ أثناء جلب قائمة الرحلات الملغاة. يرجى المحاولة مرة أخرى.';
          this.errorMessage = msg;
          this.trips = [];
        });
    },
    getCancellationTypeInfo(type) {
      if (type === 'driver_no_start') {
        return { label: 'عدم بدء الكابتن للرحلة', shortLabel: 'عدم بدء', color: 'warning' };
      }
      if (type === 'emergency') {
        return { label: 'إلغاء بسبب طارئ', shortLabel: 'طارئ', color: 'error' };
      }
      return { label: type || 'غير محدد', shortLabel: type || '-', color: 'grey' };
    },
    formatDistance(item) {
      if (!item || item.location_available === false || item.cancelled_distance_meters === null || item.cancelled_distance_meters === undefined) {
        return 'الموقع غير متاح';
      }
      const meters = Number(item.cancelled_distance_meters);
      if (isNaN(meters)) return 'الموقع غير متاح';

      if (meters < 1000) {
        return `${meters} م`;
      }
      return `${(meters / 1000).toFixed(2)} كم`;
    },
    openDetailsModal(item) {
      this.selectedItem = item;
      this.detailsDialog = true;
    },
  },
};
</script>

<style scoped>
.v-data-table >>> td {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}
</style>
